/**
 * Money at two scales.
 *
 * Every amount is computed the same way — unit price × quantity, rounded once —
 * and the API serves the result twice:
 *
 * - the **ledger** (`amount`, `rate_value`, `rounding_delta`) at four places,
 *   the settlement quantum of 0.0001; and
 * - the **invoice** (`payable_amount`, `payable_unit_price`) in whole minor
 *   units, the sum of *rounded line items* (art. 106e ust. 11 ustawy o VAT).
 *
 * The invoice amount is never the ledger amount rounded — 54.5390 rounds to
 * 54.54, but the document says 54.55 because it adds up rounded lines. Never
 * derive one from the other here; render what the API sent.
 *
 * `unit_price` is a third scale: eight places, because it is a *factor* (the
 * price of a full copy under the row's rate), not an amount. To reproduce an
 * invoice line, multiply `payable_unit_price`, never `unit_price`.
 *
 * `Number()` is lossless for all of this: a double carries 15–17 significant
 * decimal digits, so an eight-place price would have to exceed ~1e7 and a
 * four-place amount ~1e11 before a digit is lost.
 */

/** Grouping and the decimal comma; the panel is Polish regardless of currency. */
const MONEY_LOCALE = 'pl-PL';

/**
 * The stats endpoints aggregate across currencies without splitting, so their
 * payloads carry no `currency` and their cards fall back to this. Everywhere a
 * row or report has its own `currency`, pass it.
 */
const DEFAULT_CURRENCY = 'PLN';

/** `MONEY_SCALE` / `RATE_SCALE` — the ledger quantum, 0.0001. */
const LEDGER_DIGITS = 4;

/** `PRICE_SCALE` — 0.00000001. */
const UNIT_PRICE_DIGITS = 8;

/** Settlement tables render hundreds of cells; the constructor is the slow half. */
const formatters = new Map<string, Intl.NumberFormat>();

/**
 * `digits: undefined` leaves the fraction length to the currency's own minor
 * units — the mirror of the backend's `minor_units()`. That is what the invoice
 * scale is: PLN and EUR get two places, JPY none, BHD three.
 */
const money = (
  value: string | number,
  currency: string,
  digits: number | undefined,
  signed = false
): string => {
  const key = `${currency}|${digits ?? 'minor'}|${signed ? 'signed' : ''}`;
  let formatter = formatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(MONEY_LOCALE, {
      style: 'currency',
      currency,
      ...(digits != null && {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }),
      ...(signed && { signDisplay: 'exceptZero' as const }),
    });
    formatters.set(key, formatter);
  }
  return formatter.format(Number(value));
};

/**
 * A price at the currency's own scale. For values with no ledger quantum and no
 * invoice behind them — store prices, say. For settlement money, reach for
 * `formatLedger` or `formatPayable` instead.
 */
export const formatMoney = (
  value: string | number,
  currency = DEFAULT_CURRENCY
): string => money(value, currency, undefined);

/** The ledger: exactly what the rate produced, at four places. */
export const formatLedger = (
  value: string | number,
  currency = DEFAULT_CURRENCY
): string => money(value, currency, LEDGER_DIGITS);

/** The invoice: whole minor units of the currency. */
export const formatPayable = (
  value: string | number,
  currency = DEFAULT_CURRENCY
): string => money(value, currency, undefined);

/** The price of a full copy, all eight places. Detail views and tooltips. */
export const formatUnitPrice = (
  value: string | number,
  currency = DEFAULT_CURRENCY
): string => money(value, currency, UNIT_PRICE_DIGITS);

/**
 * The same price shortened to four places, trailing zeros kept — a price column
 * only reads as a column when every cell has the same shape.
 */
export const formatUnitPriceShort = (
  value: string | number,
  currency = DEFAULT_CURRENCY
): string => money(value, currency, LEDGER_DIGITS);

/**
 * A signed ledger amount: `+0,0110 zł`, `-0,0110 zł`. `signDisplay` also keeps
 * `Number('-0.0000')`, which is `-0`, from rendering as `-0,0000 zł`.
 */
export const formatLedgerDelta = (
  value: string | number,
  currency = DEFAULT_CURRENCY
): string => money(value, currency, LEDGER_DIGITS, true);

/** True for `"0.0000"`, `"-0.0000"`, `"0"` and `0` — decides whether a delta renders. */
export const isZeroAmount = (value: string | number): boolean =>
  Number(value) === 0;
